import { API } from "@/lib/api";

/**
 * Consume an SSE stream from a POST endpoint. Yields `{event, data}` frames.
 * SSE requires `text/event-stream`, and the browser's native EventSource
 * only supports GET, so we roll a minimal parser over fetch's ReadableStream.
 *
 * Usage:
 *   for await (const frame of streamSSE("/public/assistant/stream", { session_id, message })) {
 *     if (frame.event === "delta") append(frame.data);
 *     else if (frame.event === "done") { ...; break; }
 *   }
 */
export async function* streamSSE(path, body) {
  const resp = await fetch(`${API}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", "Accept": "text/event-stream" },
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    const err = new Error(text || `HTTP ${resp.status}`);
    err.status = resp.status;
    throw err;
  }
  if (!resp.body) return;
  const reader = resp.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buf = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    // Frames are separated by \n\n per SSE spec.
    let sep;
    while ((sep = buf.indexOf("\n\n")) !== -1) {
      const raw = buf.slice(0, sep);
      buf = buf.slice(sep + 2);
      let event = "message";
      const dataLines = [];
      for (const line of raw.split("\n")) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) dataLines.push(line.slice(5).replace(/^ /, ""));
      }
      yield { event, data: dataLines.join("\n") };
    }
  }
}
